/**
 * O «registo» que acompanha um relatório de erro: as últimas coisas que correram
 * mal no browser (erros da consola, exceções por apanhar, pedidos à API que
 * falharam). Fica só em memória — nada sai daqui até alguém carregar em «Enviar».
 */

export type LogLevel = "error" | "warn" | "info" | "http";
export type LogEntry = { t: string; level: LogLevel; text: string };

const MAX_ENTRIES = 100;
const MAX_TEXT = 1000;

const buffer: LogEntry[] = [];
let installed = false;

/** Tira do texto o que não deve viajar num email: tokens e palavras-passe. */
export const redact = (text: string): string =>
  text
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [oculto]")
    .replace(
      /\b(token|password|palavra-passe|authorization|secret|api[_-]?key)(["']?\s*[:=]\s*["']?)[^\s"',;&]+/gi,
      "$1$2[oculto]",
    );

export const recordLog = (level: LogLevel, text: string): void => {
  buffer.push({
    t: new Date().toISOString(),
    level,
    text: redact(text).slice(0, MAX_TEXT),
  });
  if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES);
};

const stringify = (value: unknown): string => {
  if (value instanceof Error) return `${value.name}: ${value.message}\n${value.stack ?? ""}`;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/** Um pedido à API que falhou: método, caminho (sem parâmetros) e estado. Nunca o corpo. */
export const recordHttpFailure = (info: {
  method?: string;
  url?: string;
  status?: number;
  message?: string;
}): void => {
  const path = (info.url ?? "").split("?")[0];
  recordLog(
    "http",
    `${(info.method ?? "GET").toUpperCase()} ${path} → ${info.status ?? "sem resposta"}${
      info.message ? ` (${info.message})` : ""
    }`,
  );
};

export const getLogSnapshot = (): LogEntry[] => buffer.map((entry) => ({ ...entry }));

export const clearLog = (): void => {
  buffer.length = 0;
};

/** Liga a captura (uma só vez): consola, erros por apanhar e promessas rejeitadas. */
export const installLogCapture = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  for (const level of ["error", "warn"] as const) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      recordLog(level, args.map(stringify).join(" "));
      original(...args);
    };
  }

  window.addEventListener("error", (event) => {
    recordLog(
      "error",
      `Erro não apanhado: ${event.message} (${event.filename ?? "?"}:${event.lineno ?? "?"})`,
    );
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordLog("error", `Promessa rejeitada: ${stringify(event.reason)}`);
  });
};
